import { useMemo, useState } from 'react'
import { buildCurl, buildODataQuery, pathPlaceholders, queryItems } from '../../../lib/curl.js'
import { Icon } from '../../icons/Icon.jsx'
import { MethodBadge, OpTag } from '../../ui/Badges.jsx'
import { AttachedGotchas, Card, Code, CopyButton, InlineCode, Seg, Toggle, asList } from './primitives.jsx'
import w from './widgets.module.css'

/**
 * #3 Request builder (show_endpoint). REST: inputs for path params, query and
 * includes, with live cURL. OData: a table query built from the table details.
 * When the answer has both, one card switches between them.
 */
export function RequestBuilder({ input, block }) {
  const alternate = block.alternate
  const [mode, setMode] = useState('main')
  if (input.variant === 'secondary') return <EndpointLine input={input} block={block} />

  const shown = mode === 'alternate' && alternate ? alternate : block
  const shownInput = shown.input ?? {}
  const odata = shownInput.api === 'odata'
  const switcher = alternate ? (
    <Seg
      label="API"
      options={[
        { value: 'main', label: 'Entity API' },
        { value: 'alternate', label: 'OData query' },
      ]}
      value={mode}
      onChange={setMode}
    />
  ) : null

  return (
    <Card
      label={`Endpoint ${shownInput.op_id ?? ''}`}
      title={
        <span className={w.row} style={{ gap: 8 }}>
          {shownInput.method ? <MethodBadge method={shownInput.method} /> : null}
          <span className={w.titleMono}>{odata ? `${shownInput.table ?? 'OData'} query` : shownInput.op_id}</span>
        </span>
      }
      sub={odata ? `OData ${shownInput.odata_version ?? 'v4'}` : shownInput.summary}
      right={switcher}
    >
      {odata ? (
        <ODataBuilder key={shown.key} input={shownInput} />
      ) : (
        <RestBuilder key={shown.key} input={shownInput} />
      )}
      <AttachedGotchas key={`g-${shown.key}`} items={asList(shown.gotchas)} />
    </Card>
  )
}

function OutputPane({ curl, applied = [], notes = [] }) {
  const lines = [...applied, ...asList(notes)]
  return (
    <div className={w.col} style={{ gap: 8 }}>
      <div className={w.row}>
        <span className={`${w.muted} ${w.grow}`}>cURL</span>
        <CopyButton text={curl} label="Copy cURL" />
      </div>
      <Code code={curl} />
      {lines.length ? (
        <>
          <span className="section-label" style={{ marginTop: 4 }}>
            Applied for you
          </span>
          <ul className={`${w.plainList} ${w.notes}`}>
            {lines.map((line, i) => (
              <li key={i}>
                <InlineCode text={line} />
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </div>
  )
}

// ---- REST ---------------------------------------------------------------------

function RestBuilder({ input }) {
  const examples = useMemo(
    () => Object.fromEntries(asList(input.path_params).map((p) => [p.name, p.example ?? ''])),
    [input.path_params],
  )
  const placeholders = pathPlaceholders(input.path)
  const items = queryItems(input)
  const expandOptions = asList(input.expand_options)
  const fields = asList(input.response_fields)

  const [params, setParams] = useState({})
  const [query, setQuery] = useState({})
  const [expand, setExpand] = useState([])

  const paramValues = Object.fromEntries(placeholders.map((name) => [name, params[name] ?? examples[name] ?? '']))
  const curl = buildCurl(input, { params: paramValues, query, expand })

  return (
    <div className={w.grid2}>
      <div className={w.col}>
        {placeholders.length ? (
          <div className={w.grid2} style={{ gap: 10 }}>
            {placeholders.map((name) => (
              <label key={name} className={w.field}>
                {name === 'base' ? 'Base URL' : name}
                <input
                  className={w.input}
                  value={paramValues[name]}
                  placeholder={`{${name}}`}
                  onChange={(e) => setParams((v) => ({ ...v, [name]: e.target.value }))}
                />
              </label>
            ))}
          </div>
        ) : null}
        {items.length ? (
          <div className={w.grid2} style={{ gap: 10 }}>
            {items.map((item) => (
              <label key={item.name} className={w.field}>
                <span>
                  {item.name}
                  {item.required ? <span className={w.req}>*</span> : null}
                </span>
                <input
                  className={w.input}
                  value={query[item.name] ?? (item.required ? (item.example ?? '') : '')}
                  placeholder={item.example ?? (item.required ? 'Required' : 'Optional')}
                  onChange={(e) => setQuery((v) => ({ ...v, [item.name]: e.target.value }))}
                />
              </label>
            ))}
          </div>
        ) : null}
        {expandOptions.length ? (
          <div className={w.field}>
            Include
            <div className={w.rowTight}>
              {expandOptions.map((name) => (
                <Toggle
                  key={name}
                  on={expand.includes(name)}
                  onChange={(on) => setExpand((v) => (on ? [...v, name] : v.filter((x) => x !== name)))}
                >
                  {name}
                </Toggle>
              ))}
            </div>
          </div>
        ) : null}
        {input.body_note?.field ? (
          <div className={w.muted}>
            Body: <code className={w.inlineCode}>{input.body_note.field}</code> {input.body_note.text}
          </div>
        ) : null}
        {fields.length ? (
          <div className={w.colTight}>
            <span className="section-label">Key response fields</span>
            <div className="pill-row">
              {fields.map((f) => (
                <span key={f} className="mono-pill">
                  {f}
                </span>
              ))}
            </div>
          </div>
        ) : null}
        {!placeholders.length && !items.length && !expandOptions.length && !fields.length ? (
          <div className={w.muted}>No parameters to fill in.</div>
        ) : null}
      </div>
      <OutputPane curl={curl} notes={input.notes} />
    </div>
  )
}

// ---- OData --------------------------------------------------------------------

const PAGE_SIZES = [50, 200, 500]

function weekAgo() {
  const d = new Date(Date.now() - 7 * 86_400_000)
  return d.toISOString().slice(0, 10)
}

function ODataBuilder({ input }) {
  const allColumns = asList(input.columns)
  const [company, setCompany] = useState('')
  const [columns, setColumns] = useState(() => {
    const preset = asList(input.default_columns)
    return preset.length ? preset : allColumns.slice(0, 3)
  })
  const [since, setSince] = useState(weekAgo)
  const [top, setTop] = useState(200)
  const [ids, setIds] = useState('')
  const [excludeQuotes, setExcludeQuotes] = useState(true)

  const { curl, applied } = buildODataQuery(input, { company, columns, since, top, ids, excludeQuotes })

  return (
    <div className={w.grid2}>
      <div className={w.col}>
        <div className={w.split}>
          <label className={w.field}>
            Table
            <input className={w.input} value={input.table ?? ''} disabled />
          </label>
          {input.company_column ? (
            <label className={w.field}>
              Company ({input.company_column})
              <input
                className={w.input}
                value={company}
                placeholder="e.g. ACME"
                onChange={(e) => setCompany(e.target.value)}
              />
            </label>
          ) : null}
        </div>
        {allColumns.length ? (
          <div className={w.field}>
            Columns
            <div className={w.rowTight}>
              {allColumns.map((col) => (
                <Toggle
                  key={col}
                  mono
                  on={columns.includes(col)}
                  onChange={(on) => setColumns((v) => (on ? [...v, col] : v.filter((x) => x !== col)))}
                >
                  {col}
                </Toggle>
              ))}
            </div>
          </div>
        ) : null}
        <div className={w.split}>
          {input.date_column ? (
            <label className={w.field}>
              Changed since
              <input className={w.input} type="date" value={since} onChange={(e) => setSince(e.target.value)} />
            </label>
          ) : null}
          <label className={w.field}>
            Page size
            <select className={w.input} value={top} onChange={(e) => setTop(Number(e.target.value))}>
              {PAGE_SIZES.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
        </div>
        {input.key_column ? (
          <label className={w.field}>
            Only these ids (comma separated)
            <input className={w.input} value={ids} placeholder="1048213, 1048214" onChange={(e) => setIds(e.target.value)} />
          </label>
        ) : null}
        {input.quote_filter ? (
          <div className={w.field}>
            Quotes
            <div>
              <Seg
                label="Quotes"
                options={[
                  { value: true, label: 'Exclude quotes' },
                  { value: false, label: 'Include quotes' },
                ]}
                value={excludeQuotes}
                onChange={setExcludeQuotes}
              />
            </div>
          </div>
        ) : null}
      </div>
      <OutputPane curl={curl} applied={applied} notes={input.notes} />
    </div>
  )
}

// ---- Secondary: one line, opens into the builder ---------------------------------

function EndpointLine({ input, block }) {
  const [open, setOpen] = useState(false)
  return (
    <section aria-label={`Endpoint ${input.op_id ?? ''}`} className={w.card}>
      <div className={w.endpointRow}>
        {input.method ? <MethodBadge method={input.method} size="secondary" /> : null}
        <span className={w.rowPath}>{input.path}</span>
        {input.summary ? <span className={w.rowSummary}>{input.summary}</span> : null}
        <span className={w.headRight}>
          {input.op_id ? <OpTag>{input.op_id}</OpTag> : null}
          <button
            type="button"
            className="icon-btn"
            aria-label={`${open ? 'Hide' : 'Build'} ${input.op_id ?? 'request'}`}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            style={{ width: 28, height: 28, transform: open ? 'rotate(180deg)' : undefined }}
          >
            <Icon name="chevron-down" size={16} strokeWidth={1.7} />
          </button>
        </span>
      </div>
      {open || asList(block.gotchas).length ? (
        <div className={w.body} style={{ borderTop: '1px solid var(--line-soft)' }}>
          {open ? <RestBuilder input={input} /> : null}
          <AttachedGotchas items={asList(block.gotchas)} />
        </div>
      ) : null}
    </section>
  )
}
